import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, CheckCircle2, Clock } from 'lucide-react';
import { fmtDuration } from '../lib/utils';
import { DifficultyBadge } from './DifficultyBadge';
import { useTagColors, tagChipStyle } from '../lib/tagColors';

export function LessonCard({ lesson, index = 0, compact = false }) {
  const navigate = useNavigate();
  const tagColors = useTagColors();
  const progressPct =
    lesson.duration_seconds > 0
      ? Math.min(100, Math.round(((lesson.progress_seconds || 0) / lesson.duration_seconds) * 100))
      : 0;

  return (
    <motion.button
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.03, 0.3) }}
      onClick={() => navigate(`/app/lessons/${lesson.lesson_id}`)}
      data-testid={`lesson-card-${lesson.lesson_id}`}
      className={`group text-left ${compact ? 'w-[260px] shrink-0' : 'w-full'}`}
    >
      <div className="relative aspect-video overflow-hidden rounded-xl border border-white/5 bg-ink-elevated">
        {lesson.thumbnail_url ? (
          <img
            src={lesson.thumbnail_url}
            alt={lesson.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="grid h-full w-full place-items-center text-zinc-600">
            <Play />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />

        <div className="absolute left-2.5 top-2.5 flex gap-1.5" />

        <div className="absolute bottom-2.5 right-2.5 flex items-center gap-1 rounded-md bg-black/70 px-1.5 py-0.5 text-[11px] text-zinc-200 tnum">
          <Clock size={11} /> {fmtDuration(lesson.duration_seconds)}
        </div>

        <div className="absolute bottom-2.5 left-2.5">
          {lesson.completed && (
            <span className="flex items-center gap-1 rounded-md bg-emerald/90 px-1.5 py-0.5 text-[11px] font-semibold text-black">
              <CheckCircle2 size={12} /> Done
            </span>
          )}
        </div>

        {progressPct > 0 && !lesson.completed && (
          <div className="absolute bottom-0 left-0 h-1 w-full bg-white/10">
            <div className="h-full bg-emerald" style={{ width: `${progressPct}%` }} />
          </div>
        )}
      </div>

      <div className="mt-3 space-y-1.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <DifficultyBadge level={lesson.difficulty} />
          {(lesson.tags || []).slice(0, 3).map((t) => {
            const style = tagChipStyle(tagColors[t]);
            return (
              <span
                key={t}
                style={style || undefined}
                className={style
                  ? 'rounded-full border px-2 py-0.5 text-[11px] font-medium'
                  : 'rounded-full border border-emerald/30 bg-emerald/10 px-2 py-0.5 text-[11px] font-medium text-emerald'}
              >
                {t}
              </span>
            );
          })}
        </div>
        <h3 className="font-heading font-semibold leading-snug text-white line-clamp-2 group-hover:text-emerald-glow transition-colors">
          {lesson.title}
        </h3>
        {!compact && (
          <p className="text-sm text-zinc-400 line-clamp-2">{lesson.description}</p>
        )}
      </div>
    </motion.button>
  );
}
